// REACT IMPORTS
import React, { useMemo, useRef, useState } from "react";

// ICONS IMPORTS
import { HelpIcon } from "icons/header/HelpIcon";
import { NotificationIcon } from "icons/header/NotificationIcon";
import { useIsSmallSidebar, useShowWarn } from "store/slices/commonSlice";
import Notification from "./Notification";
import { Link, useLocation, useParams } from "react-router-dom";
import useOutsideClick from "hooks/useOutsideClick";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  notificationRead,
  updateIsReadAllNotification,
  updateNotificationCount,
  useUserData,
} from "store/slices/authSlice";
import { RootState } from "store/store";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import { SettingsValue } from "types/payments";

// PROPS TYPE
interface HeaderProps {
  headerContent: any;
  className?: string;
  headerclassName?: string;
}
/* ------------------------------------- HEADER ------------------------------------- */

const Header = (props: HeaderProps) => {
  const { headerContent, className = "", headerclassName = "" } = props;
  const isWarnEnabled = useShowWarn();
  const portalSettings = usePortalSettingsData();

  // Keep the offset in sync with the warning bar rendered by the layout.
  const showWarn = useMemo(
    () =>
      isWarnEnabled &&
      (portalSettings?.min_marinda_flag === SettingsValue.CUSTOM ||
        portalSettings?.min_marinda_flag === SettingsValue.DEFAULT),
    [isWarnEnabled, portalSettings?.min_marinda_flag]
  );
  const [openNotification, setOpenNotifictaion] = useState<boolean>(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const { clusterId: cluster_id } = useParams();
  const isSmallSidebar = useIsSmallSidebar();
  const { notificationCount } = useAppSelector(
    (state: RootState) => state.auth
  );
  const location = useLocation();
  const dispatch = useAppDispatch();
  const userData = useUserData();

  useOutsideClick(notificationRef, () => {
    setOpenNotifictaion(false);
  });

  const readAllNotification = async () => {
    try {
      await dispatch(updateIsReadAllNotification(true));
      await dispatch(updateNotificationCount(0));
      let payload = {
        accounts_id: userData?.data?.accountData.id,
        is_all_read: true,
        notificartion_read: true,
        is_read: false,
      };

      await dispatch(notificationRead(payload)).unwrap();
    } catch (error) {
      console.log(" readAllNotification error ======", error);
    }
  };

  return (
    <>
      <div
        className={`fixed ${
          isSmallSidebar ? "lg:pl-[72px]" : "lg:pl-[254px]"
        } w-full lg:flex hidden z-30 bg-bgPrimary flex-col justify-center items-center left-0 animate__animated animate__fadeIn ${headerclassName} ${
          showWarn ? "lg:top-12" : "top-0"
        }`}
      >
        <div
          className={`pt-6 pb-[26px] px-10 w-full flex items-center justify-between ${className}`}
        >
          {headerContent}
          <div className="flex w-full gap-4 items-center justify-end">
            {location?.pathname?.split("/")?.[2] === "notification" && (
              <div
                className="cursor-pointer text-blue hover:!text-btnPrimaryHover text-sm underline "
                onClick={() => readAllNotification()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    readAllNotification();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Mark all notifications as read"
              >
                Mark all as read
              </div>
            )}
            <div ref={notificationRef}>
              <div
                className={`p-2 rounded-md hover:!bg-bgSecondary cursor-pointer relative ${openNotification ? "bg-bgSecondary" : ""}`}
                onClick={() => {
                  setOpenNotifictaion((state) => !state);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setOpenNotifictaion((state) => !state);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-haspopup="true"
                aria-expanded={openNotification}
                aria-label={
                  notificationCount && notificationCount > 0
                    ? `Notifications, ${notificationCount} unread`
                    : "Notifications"
                }
              >
                {notificationCount && notificationCount > 0 ? (
                  <div
                    className={`absolute top-0.5 right-0.5 left-5 ${
                      notificationCount < 10
                        ? "rounded-full h-[19px] w-[19px]"
                        : "rounded-[20px] px-1 py-0.5 w-fit h-[19px]"
                    } border-0.5 z-1 border-white bg-blue grid place-items-center`}
                  >
                    <p className="text-center text-[9.14px] leading-[11.19px] font-bold text-white">
                      {notificationCount > 99 ? "99+" : notificationCount}
                    </p>
                  </div>
                ) : null}
                <NotificationIcon
                  strokeColor={
                    // location?.pathname?.split("/")?.[2] === "notification" ||
                    openNotification
                      ? "var(--color-blue)"
                      : "var(--color-icon-obscure)"
                  }
                />
              </div>
              {openNotification && (
                <div
                  style={{
                    boxShadow: " 0px 10px 30px 0px #3C42571A",
                    // Grows to show more notifications on tall screens but is
                    // never allowed to run past the bottom of the window, so
                    // "See all notifications" always stays reachable.
                    maxHeight: "calc(var(--shell-vh) - 96px)",
                  }}
                  className={`right-10 top-[74px] absolute bg-bgPrimary z-10 w-[450px] flex flex-col`}
                >
                  <Notification setOpenNotifictaion={setOpenNotifictaion} />
                </div>
              )}
            </div>
            {/* The icon is the only content, so without a label this link is
                announced as just "link" and its purpose is unknowable to anyone
                not seeing the question mark. */}
            <Link
              to={`/${cluster_id}/support-help`}
              aria-label="Support and help"
              className={`p-2 rounded-md cursor-pointer hover:!bg-bgSecondary ${location?.pathname?.split("/")?.[2] === "support-help" ? "bg-bgSecondary" : ""}`}
            >
              <HelpIcon
                aria-hidden="true"
                strokeColor={
                  location?.pathname?.split("/")?.[2] === "support-help"
                    ? "var(--color-blue)"
                    : "var(--color-icon-obscure)"
                }
              />
            </Link>
          </div>
        </div>
        <div className="h-[1px] w-[calc(100%-80px)] bg-strokePrimary " />
      </div>
      <div className="lg:hidden">{headerContent}</div>
    </>
  );
};

export default Header;
